import { log } from '../services/production-logger';

/**
 * This utility fixes Firebase Auth logging issues in React Native.
 * Import this file once at the root of your app (e.g., in App.tsx).
 */

// Store original console methods
const originalConsoleError = console.error;
const originalConsoleWarn = console.warn;
const originalConsoleLog = console.log;

// Filter patterns that should be suppressed
const ERROR_FILTERS = [
  { contains: ['Attempted to log', '@firebase/auth'] },
  { contains: ['AsyncStorage has been extracted from react-native'] },
  { contains: ['@firebase/analytics', 'getElementsByTagName'] },
  { contains: ['TypeError: Cannot read property', 'getElementsByTagName', 'undefined'] },
];

const WARNING_FILTERS = [
  { contains: ['AsyncStorage has been extracted from react-native'] },
  { contains: ['React Native Firebase', 'FirebaseError: Firebase: Error'] },
  { contains: ['@firebase/analytics'] },
];

// Create a safer function to check if a string contains specific terms
const stringContainsTerms = (str: string | unknown, terms: string[]): boolean => {
  if (typeof str !== 'string') return false;
  return terms.every((term: string) => str.includes(term));
};

// Track if we're in the middle of logging to prevent recursion
let isLoggingError = false;

// Check if console.error has already been overridden
const isAlreadyOverridden = console.error.name === 'safeConsoleError' || 
                           console.error.toString().includes('recordError') ||
                           console.error.toString().includes('performanceService');

// Only override if not already overridden
if (!isAlreadyOverridden) {
  console.error = function safeConsoleError(...args) {
    // Prevent recursive calls
    if (isLoggingError) {
      return;
    }
    
    isLoggingError = true;
    
    try {
      // Safely check for undefined args or args that might cause "prototype of undefined" errors
      if (!args || args.length === 0 || args[0] === undefined || args[0] === null) {
        // Call original with whatever we have, but in a way that prevents "prototype" errors
        originalConsoleError.apply(console, ['[Logged error with undefined arguments]']);
        return;
      }

      // Convert to string for easier filtering
      const errorMessage = String(args[0]);

      // Check if this message should be filtered
      for (const filter of ERROR_FILTERS) {
        if (filter.contains.every(term => errorMessage.includes(term))) {
          return; // Filter out this error
        }
      }

      // Special handling for Firebase Analytics errors since they're common
      if (
        errorMessage.includes('@firebase/analytics') ||
        errorMessage.includes('getElementsByTagName') ||
        (args[0] instanceof Error &&
          args[0].message &&
          args[0].message.includes('getElementsByTagName'))
      ) {
        return; // Suppress Firebase Analytics errors
      }

      // Safely call the original function with all arguments
      originalConsoleError.apply(console, args);
    } catch (e) {
      // If the call still fails, don't try to log again to avoid recursion
      // Just silently fail
    } finally {
      isLoggingError = false;
    }
  };
} else {
  if (__DEV__) {
    log.debug('[Firebase Logger Fix] Console.error already overridden, skipping');
  }
}

// Track if we're in the middle of logging to prevent recursion
let isLoggingWarn = false;

// Override console.warn with a safer implementation
console.warn = function safeConsoleWarn(...args) {
  // Prevent recursive calls
  if (isLoggingWarn) {
    return;
  }
  
  isLoggingWarn = true;
  
  try {
    // Safely check for undefined args
    if (!args || args.length === 0 || args[0] === undefined || args[0] === null) {
      originalConsoleWarn.apply(console, ['[Logged warning with undefined arguments]']);
      return;
    }

  // Convert to string for easier filtering
  const warningMessage = String(args[0]);

  // Check if this message should be filtered
  for (const filter of WARNING_FILTERS) {
    if (filter.contains.every(term => warningMessage.includes(term))) {
      return; // Filter out this warning
    }
  }

    // Safely call the original function
    originalConsoleWarn.apply(console, args);
  } catch (e) {
    // If the call still fails, don't try to log again to avoid recursion
    // Just silently fail
  } finally {
    isLoggingWarn = false;
  }
};

// Track if we're in the middle of logging to prevent recursion
let isLoggingLog = false;

// Log that we've initialized the logger fix
if (__DEV__) {
  console.log = function safeConsoleLog(...args) {
    // Prevent recursive calls
    if (isLoggingLog) {
      return;
    }
    
    isLoggingLog = true;
    
    try {
      // Safely check for undefined args
      if (!args || args.length === 0 || args[0] === undefined || args[0] === null) {
        originalConsoleLog.apply(console, ['[Logged message with undefined arguments]']);
        return;
      }

      // Safely call the original function
      originalConsoleLog.apply(console, args);
    } catch (e) {
      // If the call still fails, don't try to log again to avoid recursion
      // Just silently fail
    } finally {
      isLoggingLog = false;
    }
  };

  // Use our new safe logging function
  log.debug('Firebase logger fix initialized');
}

export default {};
